import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from "react-router-dom";
import { verifyToken,verifyadminToken, getTokenInfo } from '../utils/auth';
import Cookies from "js-cookie";
import { useOrder } from '../Salespos/EmpOrderContext';
import { useKitchenAuth } from "../context/KitchenAuthContext";
import VideoLoader from '../components/VideoLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}
let hasVerifiedadminToken = false;

window.hasVerifiedToken = window.hasVerifiedToken || false;
window.hasVerifiedKitchenToken = window.hasVerifiedKitchenToken || false;


const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
   const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    verifyadminToken(setIsTokenValid);
  }, []);

  if (isTokenValid === null) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader />
      </div>
    );
  }

  if (!isTokenValid) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};


// Pages only a master admin may open; location admins are sent back to the dashboard
const MasterAdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  if (getTokenInfo()?.role !== "master_admin") {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

// ✅ Persist across SPA navigation and remounts

const EmpProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const { setUser, user } = useOrder();
  const location = useLocation();
  const token = Cookies.get("emptoken");

  useEffect(() => {
    // console.log(`This is my token `, token)
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        return;
      }

      if (!window.hasVerifiedToken) {
        const userData = await verifyToken(setIsTokenValid);
        if (userData) {
          setUser(userData);
          window.hasVerifiedToken = true; // ✅ persist for this SPA session
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
        return;
      }

      // Already verified previously
      if (user) {
        setIsTokenValid(true);
      } else {
        const userData = await verifyToken(setIsTokenValid);
        if (userData) {
          setUser(userData);
          setIsTokenValid(true);
        } else {
          setIsTokenValid(false);
        }
      }
    };

    checkToken();
  }, []); // Run only once per mount

  if (isTokenValid === null) return (
    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <VideoLoader />
    </div>
  );

  if (!isTokenValid) return <Navigate to="/login" state={{ from: location }} replace />;

  return <>{children}</>;
};




const PosProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
   const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const location = useLocation();
  const token = Cookies.get("emptoken");
  const { user, setUser, setToken } = useKitchenAuth();

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        window.hasVerifiedToken = false;
        setUser(null);
        setToken(null);
        return;
      }

      // ✅ Check the flag BEFORE verification
      if (window.hasVerifiedToken) {
        if (user?.restaurantId) {
          // Already verified and user context is available
          setIsTokenValid(true);
          return;
        }

        // Verified flag is set but context is missing (e.g. refresh/login redirect path)
        const verifiedUser = await verifyToken(setIsTokenValid);
        if (!verifiedUser) {
          setIsTokenValid(false);
          setUser(null);
          return;
        }

        setUser(verifiedUser);
        setIsTokenValid(true);
        return;
      }

      // Only verify if not already verified
      const verifiedUser = await verifyToken(setIsTokenValid);
      if (!verifiedUser) {
        setIsTokenValid(false);
        setUser(null);
        setToken(null);
        return;
      }

      setUser(verifiedUser);
      setToken(token);
      window.hasVerifiedToken = true; // Mark as verified
    };

    checkToken();
  }, [token, user?.restaurantId, setToken, setUser]);

  if (isTokenValid === null) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader />
      </div>
    );
  }

  if (!isTokenValid) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};


const KitchenProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const location = useLocation();
  const token = Cookies.get("emptoken");
  const { setUser, setToken } = useKitchenAuth();

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setIsTokenValid(false);
        window.hasVerifiedToken = false;
        window.hasVerifiedKitchenToken = false;
        setUser(null);
        setToken(null);
        return;
      }

      if (window.hasVerifiedKitchenToken) {
        setIsTokenValid(true);
        return;
      }

      // Always verify for kitchen to avoid stale or cross-role sessions
      const user = await verifyToken(setIsTokenValid);
      if (!user) {
        setIsTokenValid(false);
        window.hasVerifiedKitchenToken = false;
        setUser(null);
        setToken(null);
        return;
      }
      
      setUser(user);
      setToken(token);
      setIsTokenValid(true);
      window.hasVerifiedKitchenToken = true;
    };

    checkToken();
  }, []);

  if (isTokenValid === null) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <VideoLoader />
      </div>
    );
  }

  if (!isTokenValid) {
    return <Navigate to="/kitchen/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};



export { ProtectedRoute, EmpProtectedRoute, KitchenProtectedRoute,PosProtectedRoute, MasterAdminRoute }; // Export both routes
